import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'theme';

function getSystemTheme() {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function getStoredTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

// Keeps theme state in sync with the DOM attribute set early in
// public/index.html (to avoid a flash of the wrong theme), persists an
// explicit user choice to localStorage, and otherwise follows the OS
// prefers-color-scheme setting live.
export function useTheme() {
  const [theme, setThemeState] = useState(() => getStoredTheme() || getSystemTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only follow the OS setting while the user hasn't made an explicit choice.
      if (!getStoredTheme()) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}
